import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    ControlContainer,
    FormArray,
    FormControl,
    FormGroup,
    FormGroupDirective,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CustomMasks } from '@cargos/sprintpay-utils';
import { Subject, takeUntil } from 'rxjs';
import { PaidToFacilityPairComponent } from '../../../../shared/components/paid-to-facility-pair/paid-to-facility-pair.component';
import { SectionHeaderComponent } from '../../../../shared/components/section-header/section-header.component';
import { PaidToFacility, PaidToFacilityFormControls } from '../../../../shared/models/paid-to-facility.model';
import { FORM_SECTION_DESCRIPTIONS, PAYMENT_SETTINGS_FIELD_HINTS } from '../../const/form-help-text.const';
import { AppForm } from '../../models/app-form';
import { PaymentSettingsFormErrorJSON } from './models/payment-settings';
import { formPaymentSettingsErrors } from './utils/form-errors';

@Component({
    selector: 'app-payment-settings',
    imports: [
        ReactiveFormsModule,
        MatExpansionModule,
        MatInputModule,
        MatButtonModule,
        MatCardModule,
        MatSlideToggleModule,
        PaidToFacilityPairComponent,
        SectionHeaderComponent,
    ],
    templateUrl: './payment-settings-component.component.html',
    styleUrls: ['../../form-app.component.scss'],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class PaymentSettingsComponent implements OnInit, OnDestroy {
    public paymentSettingsErrors: PaymentSettingsFormErrorJSON;
    public readonly sectionDescription = FORM_SECTION_DESCRIPTIONS.paymentSettings;
    public readonly fieldHints = PAYMENT_SETTINGS_FIELD_HINTS;
    private unsubscribe$ = new Subject<void>();

    constructor(private parent: FormGroupDirective) {
        this.paymentSettingsErrors = formPaymentSettingsErrors;
    }

    ngOnInit(): void {
        this.setupPaymentFacilitiesToggleListeners();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    get parentForm(): FormGroup<AppForm> {
        return this.parent?.form;
    }

    get facilityIdControl(): FormControl<string | null> {
        return this.parentForm?.controls?.paidToFacilityId;
    }

    get paidToFacilitiesIdArray(): FormArray<FormGroup<PaidToFacilityFormControls>> {
        return this.parentForm?.controls?.paidToFacilitiesId;
    }

    get paidToFacilityGroups(): FormGroup<PaidToFacilityFormControls>[] {
        return this.paidToFacilitiesIdArray?.controls;
    }

    get paidToMultipleFacilitiesEnableControl(): FormControl<boolean | null> {
        return this.parentForm?.controls?.paidToMultipleFacilitiesEnable;
    }

    get paidToFacilityIdControl(): FormControl<string | null> {
        return this.parentForm?.controls?.paidToFacilityId;
    }

    private createPaidToFacilityGroup(initial?: Partial<PaidToFacility>): FormGroup<PaidToFacilityFormControls> {
        return new FormGroup<PaidToFacilityFormControls>({
            paidToFacilityId: new FormControl(this.coerceFacilityId(initial?.paidToFacilityId || ''), [
                Validators.required,
                Validators.pattern(CustomMasks.alphaNumeric.mask),
            ]),
            label: new FormControl(initial?.label ?? '', [Validators.required]),
        });
    }

    private coerceFacilityId(value: string | undefined): string | null {
        if (value === null || value === undefined || value === '') return null;
        if (typeof value === 'string') {
            const trimmed = value.trim();
            return trimmed === '' ? null : trimmed;
        }
        return null;
    }

    addPaidToFacility(initial?: Partial<PaidToFacility>): void {
        this.paidToFacilitiesIdArray.push(this.createPaidToFacilityGroup(initial));
    }

    removePaidToFacility(index: number): void {
        this.paidToFacilitiesIdArray.removeAt(index);
    }

    syncPaidToFacilitiesArray(paidToFacilityArray: PaidToFacility[] = []): void {
        const paidFacArray = this.paidToFacilitiesIdArray;
        while (paidFacArray.length < paidToFacilityArray.length) {
            paidFacArray.push(this.createPaidToFacilityGroup());
        }
        while (paidFacArray.length > paidToFacilityArray.length) {
            paidFacArray.removeAt(paidFacArray.length - 1);
        }

        paidToFacilityArray.forEach((item, index) => {
            paidFacArray.at(index)?.patchValue(
                {
                    paidToFacilityId: this.coerceFacilityId(item.paidToFacilityId || ''),
                    label: item.label,
                },
                { emitEvent: false }
            );
        });
    }

    private applyPaymentFacilitiesMode(allowMultiple: boolean): void {
        if (allowMultiple) {
            this.disablePaidToFacilityIdControl();

            this.paidToFacilitiesIdArray.enable({ emitEvent: false });
            if (this.paidToFacilitiesIdArray.length === 0) {
                this.addPaidToFacility({ paidToFacilityId: '', label: '' });
            }
            return;
        }

        this.paidToFacilitiesIdArray.disable({ emitEvent: false });
        this.enablePaidToFacilityIdControl();
    }

    setupPaymentFacilitiesToggleListeners(): void {
        this.paidToMultipleFacilitiesEnableControl.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((allow) => {
                this.applyPaymentFacilitiesMode(!!allow);
            });
    }

    disablePaidToFacilityIdControl(): void {
        this.paidToFacilityIdControl.disable({ emitEvent: false });
        this.paidToFacilityIdControl.clearValidators();
        this.paidToFacilityIdControl.updateValueAndValidity({
            emitEvent: false,
        });
    }

    enablePaidToFacilityIdControl(): void {
        this.paidToFacilityIdControl.enable({ emitEvent: false });
        this.paidToFacilityIdControl.setValidators([Validators.required]);
        this.paidToFacilityIdControl.updateValueAndValidity({
            emitEvent: false,
        });
    }

    getValidPaidToFacilities(): PaidToFacility[] {
        return this.paidToFacilitiesIdArray.controls.reduce((acc, group) => {
            const value = group.value;
            const paidToFacilityId = value.paidToFacilityId;
            const label = (value.label ?? '').trim();

            if (paidToFacilityId !== null && paidToFacilityId !== undefined && label.length) {
                acc.push({ paidToFacilityId, label });
            }

            return acc;
        }, [] as PaidToFacility[]);
    }

    public updatePaymentSettingsData(
        paidToFacilitiesId?: PaidToFacility[],
        paidToMultipleFacilitiesEnable?: boolean
    ): void {
        this.syncPaidToFacilitiesArray(paidToFacilitiesId ?? []);
        this.applyPaymentFacilitiesMode(!!paidToMultipleFacilitiesEnable);
    }
}
